// oxlint-disable typescript/no-explicit-any
import type { Prettify } from '../../../../lib/typeUtils';
import { createCameraSlice } from '../camera';
import type { CameraSlice } from '../camera';
import type { SliceDefinition } from '../store';
import type { CanvasHandler } from './event-hub';
import type { Feature, FeatureRoot } from './feature';

export type FeatureArray = readonly Feature<any, any, any>[];

type UnionToIntersection<U> = (
  U extends object ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

type AnyFeatureFactory = (...args: any[]) => Feature<any, any, any>;

type FeatureState<TFactory extends AnyFeatureFactory> = FeatureRoot<
  ReturnType<TFactory>
>;

export type FeatureDeps<
  TRequired extends readonly AnyFeatureFactory[] = [],
  TOptional extends readonly AnyFeatureFactory[] = [],
> = Prettify<
  CameraSlice &
    UnionToIntersection<FeatureState<TRequired[number]>> &
    Partial<UnionToIntersection<FeatureState<TOptional[number]>>>
>;

export const withCameraSlice = <
  const T extends readonly SliceDefinition<any, any>[],
>(
  slices: T
): readonly [typeof createCameraSlice, ...T] => [createCameraSlice, ...slices];

const getHandler = <F extends Feature<any, any, any>>(
  f: F
): CanvasHandler | undefined => {
  if (!f.createHandler) return undefined;
  return (f.createHandler as (c: unknown) => CanvasHandler)(f.config);
};

// Handler dispatch order matches array order — features listed first get
// priority (their shouldRun/handleEvent run first, and they can claim locks
// before later handlers). Keep this in mind when ordering the feature array
// in the provider.
export const getHandlers = <TFeatures extends FeatureArray>(
  features: TFeatures
): CanvasHandler[] =>
  features.map(getHandler).filter((h): h is CanvasHandler => h !== undefined);

export type InferCanvasStore<
  TFactory extends () => { store: { getState: () => unknown } },
> =
  ReturnType<TFactory> extends { store: { getState: () => infer S } }
    ? S
    : never;
